import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CreditCard, Loader2, ArrowLeft, Globe2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import WhatsAppIcon from "./WhatsAppIcon";
import { waLink } from "../lib/whatsapp";
import { startStrategyCallCheckout } from "../lib/payments";
import { trackEvent } from "../lib/track";
import { tr, getLang } from "../lib/i18n";
import { useBotGuard } from "../lib/botguard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = ["Prop Firm", "Brokerage", "Both / Not sure yet"];
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const fmtDay = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const fmtFull = (iso) =>
  new Date(iso).toLocaleString([], { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

const groupByLocalDay = (days) => {
  const map = new Map();
  days.forEach((d) =>
    d.slots.forEach((iso) => {
      const key = new Date(iso).toLocaleDateString("en-CA");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(iso);
    })
  );
  return Array.from(map.entries()).map(([date, slots]) => ({ date, slots: slots.sort() }));
};

const inputCls = (err) =>
  `w-full rounded-lg bg-white/[0.04] border px-4 py-3 text-sm outline-none transition-colors placeholder:text-zinc-500 ${
    err ? "border-red-500/60" : "border-white/10 focus:border-[#E5C56F]/50"
  }`;

const BookingDialog = () => {
  const bot = useBotGuard("booking");
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", location: "", goal: "", topic: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState("");
  const [days, setDays] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selDate, setSelDate] = useState("");
  const [selSlot, setSelSlot] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const onOpen = (e) => {
      const goal = e?.detail?.goal;
      if (goal && GOALS.includes(goal)) setForm((f) => ({ ...f, goal }));
      setErrors({});
      setBusy("");
      setError("");
      setStep(1);
      setSelSlot("");
      setOpen(true);
    };
    window.addEventListener("open-booking", onOpen);
    return () => window.removeEventListener("open-booking", onOpen);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/schedule/availability`);
      const grouped = groupByLocalDay(res.data.days || []);
      setDays(grouped);
      setSelDate((cur) => (grouped.some((g) => g.date === cur) ? cur : grouped[0]?.date || ""));
    } catch (e) {
      setDays([]);
    }
    setLoadingSlots(false);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = true;
    if (!form.location.trim()) e.location = true;
    if (!form.goal) e.goal = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goToSchedule = () => {
    if (!validate()) return;
    setError("");
    setStep(2);
    loadSlots();
  };

  const saveBooking = async (path) => {
    try {
      await axios.post(`${API}/bookings`, {
        ...bot.fields(),
        name: form.name.trim(),
        email: form.email.trim(),
        goal: form.goal,
        path,
        location: form.location.trim() || null,
        topic: form.topic.trim() || null,
        slot_iso: selSlot || null,
        chat_session_id: sessionStorage.getItem("ahjin_chat_session") || null,
      });
      return true;
    } catch (e) {
      if (e.response?.status === 400) {
        setError(e.response.data?.detail || tr("Please try again."));
        bot.reset();
        return false;
      }
      console.warn("[booking] alert failed (non-blocking):", e?.message || e);
      return true;
    }
  };

  const payNow = async () => {
    if (!selSlot) {
      setError(tr("Please select a day and time for your call."));
      return;
    }
    setBusy("pay");
    setError("");
    if (!(await saveBooking("stripe"))) { setBusy(""); return; }
    const r = await startStrategyCallCheckout(null, {
      email: form.email.trim(),
      name: form.name.trim(),
      goal: form.goal,
      location: form.location.trim(),
      topic: form.topic.trim(),
      slot: selSlot,
      chatSessionId: sessionStorage.getItem("ahjin_chat_session") || null,
    });
    if (r && r.error) {
      setError(r.error);
      setBusy("");
      if (r.conflict) {
        setSelSlot("");
        loadSlots();
      }
    }
  };

  const viaWhatsApp = async () => {
    setBusy("wa");
    trackEvent("whatsapp", "booking-dialog");
    if (!(await saveBooking("whatsapp"))) { setBusy(""); return; }
    const timePart = selSlot ? ` Preferred time: ${fmtFull(selSlot)} (${LOCAL_TZ}).` : "";
    const msg =
      getLang() === "es"
        ? `Hola Ah-Jin Group — quiero reservar la consulta de $250 (30 min). Nombre: ${form.name.trim()}. Ubicación: ${form.location.trim()}. Objetivo: ${form.goal}. Email: ${form.email.trim()}.${timePart}`
        : `Hi Ah-Jin Group — I'd like to book the $250 consultation (30 min). Name: ${form.name.trim()}. Based in: ${form.location.trim()}. Goal: ${form.goal}. Email: ${form.email.trim()}.${timePart}`;
    window.open(waLink(msg), "_blank", "noopener");
    setBusy("");
    setOpen(false);
  };

  const activeDay = days.find((d) => d.date === selDate);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0a1128] border border-white/10 text-zinc-100 w-[calc(100vw-2rem)] sm:max-w-md max-h-[92vh] overflow-y-auto overflow-x-hidden rounded-xl" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-zinc-100">
            {step === 1 ? tr("Book your $250 Strategy Call") : tr("Pick a day & time")}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {step === 1
              ? tr("30 minutes · a written launch map · the full $250 credited toward any engagement.")
              : `${tr("Times shown in your timezone")} (${LOCAL_TZ}).`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 mt-2">
            <input
              name="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder={tr("Your full name")}
              className={inputCls(errors.name)}
              data-testid="booking-name-input"
            />
            <input
              name="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder={tr("Email address")}
              type="email"
              className={inputCls(errors.email)}
              data-testid="booking-email-input"
            />
            <input
              name="location"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder={tr("Where are you based? (city, country)")}
              className={inputCls(errors.location)}
              data-testid="booking-location-input"
            />
            <Select value={form.goal} onValueChange={(v) => update("goal", v)}>
              <SelectTrigger
                className={`w-full rounded-lg bg-white/[0.04] border px-4 py-3 text-sm h-auto ${errors.goal ? "border-red-500/60" : "border-white/10"} ${form.goal ? "text-zinc-100" : "text-zinc-500"}`}
                data-testid="booking-goal-select"
              >
                <SelectValue placeholder={tr("What do you want to launch?")} />
              </SelectTrigger>
              <SelectContent className="bg-[#0a1128] border border-white/10 text-zinc-100">
                {GOALS.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`booking-goal-${g.split(" ")[0].toLowerCase()}`}>
                    {tr(g)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <textarea
              name="topic"
              value={form.topic}
              onChange={(e) => update("topic", e.target.value)}
              placeholder={tr("What would you like to discuss? (optional)")}
              rows={3}
              className={`${inputCls(false)} resize-none`}
              data-testid="booking-topic-input"
            />
            <button
              type="button"
              onClick={goToSchedule}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1405] hover:brightness-110 transition-all shadow-[0_0_28px_rgba(229,197,111,0.25)]"
              data-testid="booking-continue-btn"
            >
              {tr("Continue — choose a time")}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 mt-1">
            {loadingSlots && (
              <div className="flex items-center justify-center py-10" data-testid="booking-slots-loading">
                <Loader2 size={26} className="animate-spin text-[#E5C56F]" />
              </div>
            )}

            {!loadingSlots && days.length === 0 && (
              <div className="text-sm text-zinc-400 py-4 text-center" data-testid="booking-no-slots">
                {tr("No online slots available right now — book directly on WhatsApp below.")}
              </div>
            )}

            {!loadingSlots && days.length > 0 && (
              <>
                <Select value={selDate} onValueChange={(v) => { setSelDate(v); setSelSlot(""); }}>
                  <SelectTrigger
                    className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-4 py-3 text-sm h-auto text-zinc-100"
                    data-testid="booking-day-select"
                  >
                    <SelectValue placeholder={tr("Choose a day")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a1128] border border-white/10 text-zinc-100 max-h-64">
                    {days.map((d) => (
                      <SelectItem key={d.date} value={d.date} data-testid={`booking-day-${d.date}`}>
                        {fmtDay(d.date)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-4 gap-1.5 w-full min-w-0" data-testid="booking-slot-grid">
                  {(activeDay?.slots || []).map((iso) => (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => { setSelSlot(iso); setError(""); }}
                      className={`rounded-md border px-1 py-1.5 text-[11px] tabular-nums transition-colors ${
                        selSlot === iso
                          ? "border-[#E5C56F] bg-[#E5C56F]/15 text-[#E5C56F]"
                          : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-white/25"
                      }`}
                      data-testid={`booking-slot-${iso}`}
                    >
                      {fmtTime(iso)}
                    </button>
                  ))}
                </div>
                {selSlot && (
                  <div className="flex items-center gap-2 text-xs text-zinc-300 rounded-lg border border-[#E5C56F]/25 bg-[#E5C56F]/[0.06] px-3 py-2.5" data-testid="booking-selected-slot">
                    <Globe2 size={13} className="text-[#E5C56F] shrink-0" />
                    <span>{fmtFull(selSlot)} · {tr("30 minutes")}</span>
                  </div>
                )}
              </>
            )}

            {bot.guard}

            {error && (
              <div className="text-xs text-red-400 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5" data-testid="booking-error">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={payNow}
              disabled={!!busy || loadingSlots || days.length === 0}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1405] hover:brightness-110 transition-all shadow-[0_0_28px_rgba(229,197,111,0.25)] disabled:opacity-60"
              data-testid="booking-pay-btn"
            >
              {busy === "pay" ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
              {selSlot ? `${tr("Confirm & Pay $250")} — ${fmtTime(selSlot)}` : tr("Confirm & Pay $250")}
            </button>
            <button
              type="button"
              onClick={viaWhatsApp}
              disabled={!!busy}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-full border border-purple-500/40 bg-purple-600/10 px-6 py-3 text-[12px] uppercase tracking-[0.2em] text-purple-200 hover:border-purple-400/60 hover:bg-purple-600/20 transition-all disabled:opacity-60"
              data-testid="booking-whatsapp-btn"
            >
              {busy === "wa" ? <Loader2 size={15} className="animate-spin" /> : <WhatsAppIcon size={15} />}
              {tr("Book via WhatsApp")}
            </button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors"
                data-testid="booking-back-btn"
              >
                <ArrowLeft size={12} /> {tr("Back")}
              </button>
              <div className="text-[11px] text-zinc-500">
                {tr("Secure payment by Stripe · calendar invite by email")}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
